import { CONTENT_TYPE, MODULES } from 'common/constants';
import {
	CONTROL_5W_TYPE,
	PLAIN_ENGLISH_SUMMARY_TYPE,
	REGULATION_SUMMARY_TYPE,
} from 'common/constants/AISimulationConstants';
import localStorageWrapper from 'common/loginUtils/local-storage-wrapper';
import {
	convertObjectToQueryParam,
	fetchBatchesFromArray,
	getHeader,
} from 'common/utils/utilFunctions';

import { IChatHistory } from 'features/OpenRiskGPT/api/type-def';
import {
	IRegInvSearchParams,
	IRegulationInventoryResponse,
	ITablePaginationParams,
} from 'features/RegulationInventory/api/type-def';
import {
	IAddRelationMappingErrorResponse,
	IAddRelationshipMapping,
	ICoveredProductRes,
	ICreateUpdateTask,
} from 'features/RiskProfile/Taxonomy/api/type-def';
import { IRisksRefTaxonomyTable } from 'features/RiskProfile/Taxonomy/RiskReferenceTaxonomy/api/type-def';

import apiWrapper from './api-wrapper';
import {
	IAPIErrorContent,
	ICreateTask,
	IFiveWResponse,
	ITask,
	IExportDataPayload,
	IFeedback,
	IFeedbackDelete,
	IFilterKeysResponse,
	IFilterKeysParams,
	IAllFilterType,
	IAllFilterResp,
} from './type-def';

const {
	REACT_APP_AUTH_USER_SERVICE_URL,
	REACT_APP_SSO_AUTH_USER_SERVICE_URL,
	REACT_APP_USER_SERVICE_URL,
	REACT_APP_REG_CHANGE_DOMAIN_SERVICE_URL,
	REACT_APP_ENVIRONMENT,
} = process.env;
const { DATA_INGESTION } = MODULES;

const {
	RISK_REFERENCE_TAXONOMY,
	HORIZON_SCAN_AND_ALERTS,
	REQUIREMENT_INVENTORY,
} = MODULES;
const genAiServiceUrl = process.env.REACT_APP_GEN_AI_SERVICE_URL;
const { JSON } = CONTENT_TYPE;

/**
 * Deletes a related entity.
 *
 * @param childId - The ID of the child entity.
 * @param parentId - The ID of the parent entity.
 * @returns A Promise that resolves to the response from the API.
 */
export const deleteRelatedEntity = async (
	childId: string,
	parentId: string,
	module: string,
	email: string,
	isProductSelected?: boolean,
) => {
	const baseUrl = `${REACT_APP_REG_CHANGE_DOMAIN_SERVICE_URL}/relation-mapping/relationship/${childId}/${parentId}`;
	const url =
		module === RISK_REFERENCE_TAXONOMY && !isProductSelected
			? `${baseUrl}?module=PRC inventory&email=${email}`
			: baseUrl;
	const resp = await apiWrapper.delete<any>(url, {});
	return resp;
};

export const addRelationshipMapping = async (
	data: IAddRelationshipMapping[],
) => {
	const batches = fetchBatchesFromArray(data, 50);
	const promises = batches.map((batch: any) =>
		apiWrapper.post<
			| IAddRelationshipMapping
			| ICoveredProductRes[]
			| IAddRelationMappingErrorResponse,
			IAPIErrorContent
		>(
			`${REACT_APP_REG_CHANGE_DOMAIN_SERVICE_URL}/relation-mapping/relationship`,
			batch,
		),
	);
	const responses = await Promise.all(promises);
	return responses[responses.length - 1];
};

export const onApplicationLogout = async () => {
	const ssoType = localStorageWrapper.getStorageItem('ssoType');
	const resp = await apiWrapper.get<any, IAPIErrorContent>(
		`${
			ssoType === 'v1'
				? REACT_APP_AUTH_USER_SERVICE_URL
				: REACT_APP_SSO_AUTH_USER_SERVICE_URL
		}/logout`,
	);
	return resp;
};

export const refreshTokenAPI = async () => {
	const ssoType = localStorageWrapper.getStorageItem('ssoType');
	const resp = await apiWrapper.get<any, IAPIErrorContent>(
		`${
			ssoType === 'v1'
				? REACT_APP_AUTH_USER_SERVICE_URL
				: REACT_APP_SSO_AUTH_USER_SERVICE_URL
		}/refresh`,
	);
	return resp;
};

export const getControlValidationTerms = async (data: string) => {
	const postData = {
		controlDescription: data,
	};
	const resp = await apiWrapper.post<IFiveWResponse>(
		`${REACT_APP_REG_CHANGE_DOMAIN_SERVICE_URL}/fivew-assessment/control`,
		postData,
	);
	return resp;
};

export const createTask = async (data: ICreateUpdateTask[] | ITask[]) => {
	const postData = {
		taskRequestList: data,
	};
	const resp = await apiWrapper.post<ICreateTask, IAPIErrorContent>(
		`${REACT_APP_REG_CHANGE_DOMAIN_SERVICE_URL}/task-manager/task`,
		postData,
	);
	return resp;
};

export const saveUserRecentActivity = async (data: IRisksRefTaxonomyTable) => {
	const resp = await apiWrapper.post<IRisksRefTaxonomyTable[]>(
		`${REACT_APP_USER_SERVICE_URL}/activity`,
		data,
	);
	return resp;
};

export const getRegulationInventoryData = async (
	data: IRegInvSearchParams,
	params: ITablePaginationParams,
	module?: string,
) => {
	const sortKey =
		module === DATA_INGESTION
			? params?.sortKey ?? 'activityDate'
			: params?.sortKey ?? 'reviewByDate';
	const sortDirection = params?.sortDirection ?? 'desc';

	const sort = sortKey !== '' ? `${sortKey},${sortDirection}` : '';
	const resp = await apiWrapper.post<
		IRegulationInventoryResponse,
		IAPIErrorContent
	>(
		`${REACT_APP_REG_CHANGE_DOMAIN_SERVICE_URL}/elastic-search/reginv-search?pageNumber=${params?.pageNumber}&pageSize=${params?.pageSize}&sort=${sort}`,
		data,
	);
	return resp;
};

export const getExportColNames = async (name: string) => {
	const resp = await apiWrapper.get<any, IAPIErrorContent>(
		`${REACT_APP_REG_CHANGE_DOMAIN_SERVICE_URL}/elastic-search/config?name=${name}`,
	);
	return resp;
};

export const getExportData = async (
	data: IExportDataPayload,
	moduleType?: string,
) => {
	let url;
	switch (moduleType) {
		case RISK_REFERENCE_TAXONOMY:
			url = `${REACT_APP_REG_CHANGE_DOMAIN_SERVICE_URL}/elastic-search/export`;
			break;
		case REQUIREMENT_INVENTORY:
			url = `${REACT_APP_REG_CHANGE_DOMAIN_SERVICE_URL}/elastic-search/reg-inv/export`;
			break;
		case HORIZON_SCAN_AND_ALERTS:
			url = `${REACT_APP_REG_CHANGE_DOMAIN_SERVICE_URL}/elastic-search/horizon-scanning/export`;
			break;
		default:
			url = '';
			break;
	}
	const resp = await apiWrapper.post<any, IAPIErrorContent>(url, data);
	return resp;
};

export const postMessageFeedBackData = async (
	messageId: string,
	feedBackData: any,
) => {
	const resp = await apiWrapper.post<IChatHistory | IAPIErrorContent>(
		`${genAiServiceUrl}/chat/messages/${messageId}/feedback`,
		{ ...feedBackData },
		JSON,
		getHeader(),
	);
	return resp;
};

export const putMessageFeedBackData = async (
	messageId: string,
	feedBackData: any,
) => {
	const resp = await apiWrapper.patch<IChatHistory | IAPIErrorContent>(
		`${genAiServiceUrl}/chat/messages/${messageId}/feedback`,
		{ ...feedBackData },
		JSON,
		getHeader(),
	);
	return resp;
};

export const postModelFeedbackData = async (data: any) => {
	const resp = await apiWrapper.post<IFeedback | IAPIErrorContent>(
		`${genAiServiceUrl}/feedback`,
		{ ...data },
		JSON,
		getHeader(),
	);
	return resp;
};
export const putModelFeedbackData = async (
	feedbackId: string,
	feedBackData: any,
) => {
	const resp = await apiWrapper.patch<IFeedback | IAPIErrorContent>(
		`${genAiServiceUrl}/feedback/${feedbackId}`,
		{ ...feedBackData },
		JSON,
		getHeader(),
	);
	return resp;
};

export const getFeedbackData = async (
	feedbackType: string,
	reference: string | undefined,
) => {
	const resp = await apiWrapper.get<IFeedback[], IAPIErrorContent>(
		`${genAiServiceUrl}/feedback/${feedbackType}?reference=${reference}`,
		{},
		JSON,
		getHeader(),
	);
	return resp;
};

export const deleteFeedbackData = async (id: string) => {
	const resp = await apiWrapper.delete<IFeedbackDelete, IAPIErrorContent>(
		`${genAiServiceUrl}/feedback/${id}`,
		{},
		{},
		JSON,
		getHeader(),
	);
	return resp;
};

export const getFilterHeaderValues = async (params: IFilterKeysParams) => {
	const url = params.viewType
		? `${REACT_APP_REG_CHANGE_DOMAIN_SERVICE_URL}/elastic-search/filter/${params.module}/${params.dataSource}?viewType=${params.viewType}`
		: `${REACT_APP_REG_CHANGE_DOMAIN_SERVICE_URL}/elastic-search/filter/${params.module}/${params.dataSource}`;
	const resp = await apiWrapper.get<IFilterKeysResponse[], IAPIErrorContent>(
		url,
	);
	return resp;
};

export const getFiltersValues = async (
	data: IAllFilterType,
	module: string,
) => {
	const { searchText, index, relationship, source } = data;
	const paramsObj = {
		searchText,
		index:
			module === RISK_REFERENCE_TAXONOMY
				? index
				: `${index}-${REACT_APP_ENVIRONMENT}`,
		relationship,
		source,
	};

	const resp = await apiWrapper.post<IAllFilterResp, IAPIErrorContent>(
		`${REACT_APP_REG_CHANGE_DOMAIN_SERVICE_URL}/elastic-search/filter-values`,
		paramsObj,
	);
	return resp;
};

export const uploadControlQualityFiveWData = async (
	data: any,
	formData: FormData,
	filesInfo: any,
	type: string,
	timeZone: string,
) => {
	const mappingParam = convertObjectToQueryParam(data);

	const params = new URLSearchParams({
		columnMapping: mappingParam,
		dataType: type,
		userTimeZone: timeZone,
	});

	let url;
	switch (type) {
		case REGULATION_SUMMARY_TYPE:
			url = `${REACT_APP_REG_CHANGE_DOMAIN_SERVICE_URL}/simulationarena/upload/regulation-summary?${params.toString()}`;
			break;
		case PLAIN_ENGLISH_SUMMARY_TYPE:
			url = `${REACT_APP_REG_CHANGE_DOMAIN_SERVICE_URL}/simulationarena/upload/plain-english-summary?${params.toString()}`;
			break;
		case CONTROL_5W_TYPE:
			url = `${REACT_APP_REG_CHANGE_DOMAIN_SERVICE_URL}/simulationarena/upload/fivew-assessment?${params.toString()}`;
			break;
		default:
			url = '';
			break;
	}

	const resp = await apiWrapper.post<any>(
		url,
		formData,
		CONTENT_TYPE.MULTIPART,
	);
	return resp;
};
